import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  IconButton,
  Box,
  Divider,
  Chip,
  FormHelperText,
  Alert,
  Link,
  CircularProgress,
  Paper,
  Collapse,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import LabelIcon from '@mui/icons-material/Label';
import PublicIcon from '@mui/icons-material/Public';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import faqService from '../services/faqService';
import tagService from '../services/tagService';
import { hasFeature } from '../utils/features';
import ImageViewer from './ImageViewer';

// Uniform neutral chip styling — text varies, color does not.
// Matches the "single-style chip" rule from plan 068 day 4 design.
const NEUTRAL_CHIP_SX = {
  bgcolor: '#f5f5f5',
  color: 'rgba(0,0,0,0.72)',
  border: '1px solid rgba(0,0,0,0.12)',
  fontWeight: 500,
  fontSize: '12px',
  height: '24px'
};

const AUDIO_STATUS_LABELS = {
  ready: '已就緒',
  stale: '需更新',
  pending: '排隊中',
  generating: '產生中',
  failed: '失敗',
  missing: '未產生'
};

// Preview URL is built backend-side (faq-audio.service.buildPublicUrl) and
// shipped on the FAQ payload as `audioPublicUrl`. Backend also emits it on
// socket faq_audio_status events when status flips to 'ready'. Pre-068 manual
// paths (qa_audio/<name> with no S3 mirror) get null and surface the
// 「已部署於電話系統，無線上預覽」 hint instead.

const FaqEditDialog = ({ open, onClose, faq, onSave, isLoading = false }) => {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [category, setCategory] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [questionError, setQuestionError] = useState('');
  const [answerError, setAnswerError] = useState('');
  const [imageError, setImageError] = useState('');

  // 標籤回答卡片狀態
  // 每張卡片: { type: 'public' | 'tag', tagId?, tagName?, tagColor?, answer: string, images: [], existingImageIds: [] }
  const [answerCards, setAnswerCards] = useState([]);
  const [originalAnswerCards, setOriginalAnswerCards] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [showAnswerPicker, setShowAnswerPicker] = useState(false);
  const [showNewTagForm, setShowNewTagForm] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#607D8B');
  const [tagLoading, setTagLoading] = useState(false);

  // States for ImageViewer
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [imageViewerIndex, setImageViewerIndex] = useState(0);
  const [imageViewerImages, setImageViewerImages] = useState([]);

  // 語音狀態（Plan 068 Day 4）— 從 faq prop 初始化，再由 socket 即時更新。
  // null = 未產生 (treated as 'missing' in the UI).
  const [audioInfo, setAudioInfo] = useState({
    status: null,        // ready | stale | pending | generating | failed | null
    filePath: null,
    duration: null,
    publicUrl: null,     // CloudFront URL for in-browser preview (plan-068 paths only)
    updatedAt: null,     // ms-precision timestamp used as cache-bust ?v=
    error: null
  });
  const [audioBusy, setAudioBusy] = useState(false);
  const [audioError, setAudioError] = useState('');

  // Refs
  const questionInputRef = useRef(null);
  const fileInputRefs = useRef({});

  // URL detection regex
  const urlRegex = /(https?:\/\/[^\s]+)/g;

  // 載入圖片 proxy URLs（公開回答圖片）
  const loadPublicImages = async (faqData) => {
    try {
      let imageIds = [];
      let existingImages = [];

      if (faqData.images && Array.isArray(faqData.images)) {
        existingImages = faqData.images;
        imageIds = faqData.images.map(img => img.id).filter(id => id);
      } else if (faqData.imageIds && Array.isArray(faqData.imageIds)) {
        imageIds = faqData.imageIds;
        existingImages = imageIds.map(id => ({ id }));
      }

      if (imageIds.length === 0) return [];

      // 先建立暫時 loading 狀態
      const loadingImages = existingImages.map(img => ({
        id: img.id,
        url: `/api/images/proxy/${img.id}`,
        name: img.name || img.filename || `image-${img.id}`,
        size: img.size || img.fileSize || 0,
        isExisting: true,
        loading: true
      }));

      // 取得 proxy URLs
      try {
        const imageUrls = await faqService.getBatchImageProxyUrls(imageIds, false);
        return imageUrls.map((urlData, index) => ({
          id: urlData.id,
          url: urlData.url,
          name: existingImages[index]?.name || existingImages[index]?.filename || `image-${urlData.id}`,
          size: existingImages[index]?.size || existingImages[index]?.fileSize || 0,
          isExisting: true,
          loading: false
        }));
      } catch {
        return loadingImages.map(img => ({ ...img, loading: false }));
      }
    } catch {
      return [];
    }
  };

  // 載入標籤回答的圖片 proxy URLs
  const loadTagAnswerImages = async (tagAnswerImages) => {
    if (!tagAnswerImages || tagAnswerImages.length === 0) return [];

    const imageIds = tagAnswerImages.map(img => img.id).filter(id => id);
    if (imageIds.length === 0) return [];

    try {
      const imageUrls = await faqService.getBatchImageProxyUrls(imageIds, false);
      return imageUrls.map((urlData, index) => ({
        id: urlData.id,
        url: urlData.url,
        name: tagAnswerImages[index]?.filename || `image-${urlData.id}`,
        size: tagAnswerImages[index]?.fileSize || 0,
        isExisting: true,
        loading: false
      }));
    } catch {
      return tagAnswerImages.map(img => ({
        id: img.id,
        url: `/api/images/proxy/${img.id}`,
        name: img.filename || `image-${img.id}`,
        size: img.fileSize || 0,
        isExisting: true,
        loading: false
      }));
    }
  };

  // Reset form when dialog opens with new data
  useEffect(() => {
    if (open && faq) {
      setQuestion(faq.question || '');
      setAnswer(faq.answer || '');
      setCategory(faq.category || '');
      setQuestionError('');
      setAnswerError('');
      setImageError('');
      setShowAnswerPicker(false);
      setShowNewTagForm(false);
      setNewTagName('');
      setNewTagColor('#607D8B');

      // Sync audio state from the row payload. Backend uses snake_case for
      // some FAQ list endpoints, so accept both spellings.
      setAudioInfo({
        status: faq.audioStatus ?? faq.audio_status ?? null,
        filePath: faq.audioFilePath ?? faq.audio_file_path ?? null,
        duration: faq.audioDuration ?? faq.audio_duration ?? null,
        publicUrl: faq.audioPublicUrl ?? faq.audio_public_url ?? null,
        updatedAt: faq.audioUpdatedAt ?? faq.audio_updated_at ?? null,
        error: null
      });
      setAudioError('');
      setAudioBusy(false);

      // Parse keywords
      let parsedKeywords = [];
      try {
        if (faq.raw_keywords) {
          if (typeof faq.raw_keywords === 'string') {
            parsedKeywords = JSON.parse(faq.raw_keywords);
          } else if (Array.isArray(faq.raw_keywords)) {
            parsedKeywords = faq.raw_keywords;
          }
        } else if (faq.raw_keyword) {
          if (typeof faq.raw_keyword === 'string') {
            parsedKeywords = JSON.parse(faq.raw_keyword);
          } else if (Array.isArray(faq.raw_keyword)) {
            parsedKeywords = faq.raw_keyword;
          }
        } else if (faq.keywords && Array.isArray(faq.keywords)) {
          parsedKeywords = faq.keywords;
        }
      } catch (e) {
        parsedKeywords = [];
      }
      setKeywords(parsedKeywords);

      // 載入可用標籤和標籤回答（含圖片）
      const loadTagData = async () => {
        try {
          const tagsRes = await tagService.getTags();
          setAvailableTags(tagsRes.data || tagsRes || []);
        } catch (e) {
          setAvailableTags([]);
        }

        const cards = [];

        // 載入標籤回答（編輯模式）
        let tagAnswerData = [];
        if (faq?.id) {
          try {
            const tagAnswerRes = await faqService.getFaqTagAnswers(faq.id);
            tagAnswerData = tagAnswerRes?.data?.tagAnswers || [];
          } catch (e) {
            tagAnswerData = [];
          }
        }

        // 公開回答卡片（含公開圖片）
        if (!faq?.id || faq?.answer) {
          const publicImages = await loadPublicImages(faq);
          cards.push({
            type: 'public',
            answer: faq?.answer || '',
            images: publicImages,
            existingImageIds: publicImages.filter(img => img.isExisting).map(img => img.id)
          });
        }

        // 標籤回答卡片（含各自圖片）
        for (const ta of tagAnswerData) {
          const tagImages = await loadTagAnswerImages(ta.images || []);
          cards.push({
            type: 'tag',
            tagId: ta.tagId || ta.tag_id,
            tagName: ta.tag?.name || ta.Tag?.name || ta.tagName || '',
            tagColor: ta.tag?.color || ta.Tag?.color || ta.tagColor || '#607D8B',
            answer: ta.answer || '',
            images: tagImages,
            existingImageIds: tagImages.filter(img => img.isExisting).map(img => img.id)
          });
        }

        // 新建 FAQ 至少要有一張公開卡片
        if (cards.length === 0) {
          cards.push({ type: 'public', answer: '', images: [], existingImageIds: [] });
        }

        setAnswerCards(cards);
        setOriginalAnswerCards(JSON.parse(JSON.stringify(cards)));
      };

      loadTagData();
    }
  }, [open, faq]);

  // Live audio status updates flow through the parent FaqPage's dedicated
  // io() socket, which patches both the table row AND the currentFaq state
  // we're rendering. The reset useEffect above re-syncs audioInfo from the
  // updated faq prop on every change. We deliberately do NOT subscribe via
  // socketService.on here — the shared wrapper has a known registration race
  // that silently drops events (per memory feedback_dedicated_socket_for_live_features).
  // Same pattern we used to fix ConstraintEditDialog.
  //
  // We still need a local audioBusy reset when the row's status leaves the
  // in-flight states. That happens via the useEffect that re-runs on faq
  // changes — when the parent's patch sets faq.audioStatus to 'ready' /
  // 'failed' / 'stale', the reset block above flips audioBusy=false.
  useEffect(() => {
    if (!audioBusy) return;
    const status = audioInfo.status;
    if (status && status !== 'pending' && status !== 'generating') {
      setAudioBusy(false);
    }
  }, [audioBusy, audioInfo.status]);

  // Detect whether the dialog has unsaved edits. ElevenLabs-style: clicking
  // 重新產生語音 should commit pending text edits before enqueuing the regen,
  // so the new audio reflects what the user just typed.
  const isFormDirty = () => {
    if (!faq) return false;
    const publicCard = answerCards.find(c => c.type === 'public');
    const currentAnswer = publicCard?.answer || '';
    const originalAnswer = faq.answer || '';
    if (question !== (faq.question || '')) return true;
    if (currentAnswer !== originalAnswer) return true;
    if (category !== (faq.category || '')) return true;
    // Compare answer cards (covers tag answer edits + new images)
    if (JSON.stringify(answerCards) !== JSON.stringify(originalAnswerCards)) return true;
    return false;
  };

  // 只有「公開答案」文字變了，後端才會在儲存後自動重生語音
  // （音檔是從 answer 合成的；改問題／關鍵字／圖片不會觸發，GPU 不白跑）。
  // 與 faq-crud.controller.updateFaq 的 answerChanged 判斷保持一致。
  const isAnswerDirty = () => {
    if (!faq) return false;
    const publicCard = answerCards.find(c => c.type === 'public');
    return (publicCard?.answer || '') !== (faq.answer || '');
  };

  // Click handler for the audio panel button. Two distinct flows:
  //
  // CREATE mode (no faq.id): full save with the _keepOpen flag. Parent
  // FaqPage.handleSaveFaq sees the flag and instead of closing the dialog,
  // updates currentFaq with the new row (incl. id + audio_status='pending').
  // The reset useEffect re-runs, the audio panel transitions to edit mode
  // in-place, and the chip animates pending → generating → ready inline.
  //
  // EDIT mode (faq.id exists): the button is disabled while form is dirty
  // (option b — user must click 儲存 first to commit text edits). When clean,
  // we hit ONLY the regen endpoint — no full updateFaq, no embedding cost,
  // no keyword regeneration. This is the responsibility split the user asked
  // for: 儲存 = full save (with embedding/keywords); audio button = audio only.
  const handleRegenerateAudio = async () => {
    if (audioBusy) return;
    setAudioError('');
    setAudioBusy(true);
    try {
      if (!faq?.id) {
        if (!validateForm()) {
          setAudioBusy(false);
          return;
        }
        const payload = buildSaveData();
        payload._keepOpen = true; // tell parent: keep dialog open after create
        await Promise.resolve(onSave(payload));
        // Parent will update faq prop with the new row; the reset useEffect
        // will then sync audioInfo to status='pending'. No further action.
        return;
      }
      // Edit mode: button is disabled when dirty so we know form is clean.
      // If somehow called while dirty (race), bail with hint.
      if (isFormDirty()) {
        setAudioBusy(false);
        setAudioError('請先按「儲存」將修改寫入後再產生語音');
        return;
      }
      await faqService.regenerateAudio(faq.id, { force: true });
      // Optimistic flip — the parent's socket handler will patch currentFaq
      // shortly and the reset useEffect will re-sync. If the socket is dead,
      // the user still sees that the click registered.
      setAudioInfo((prev) => ({ ...prev, status: 'pending', error: null }));
    } catch (err) {
      setAudioBusy(false);
      setAudioError(err.message || '重新合成語音時發生錯誤');
    }
  };

  // Validate form
  const validateForm = () => {
    let isValid = true;

    if (!question.trim()) {
      setQuestionError('問題不能為空');
      isValid = false;
    } else {
      setQuestionError('');
    }

    const hasAnswer = answerCards.some(c => c.answer && c.answer.trim());
    if (!hasAnswer) {
      setAnswerError('至少需要一個回答（公開或標籤回答）');
      isValid = false;
    } else {
      setAnswerError('');
    }

    return isValid;
  };

  // Build the save payload from current dialog state. Extracted so that
  // both the 儲存 button and the 重新產生語音 click can produce the same
  // payload shape (the latter saves before regenerating).
  const buildSaveData = () => {
    const publicCard = answerCards.find(c => c.type === 'public');
    const tagCards = answerCards.filter(c => c.type === 'tag');

    // 公開回答的新圖片和已存在圖片
    const publicNewImages = (publicCard?.images || []).filter(img => img.file && !img.isExisting);
    const publicExistingImageIds = (publicCard?.images || []).filter(img => img.isExisting).map(img => img.id);

    return {
      id: faq?.id,
      companyId: faq?.companyId,
      question,
      answer: publicCard?.answer || '',
      category,
      keywords: keywords,
      raw_keywords: JSON.stringify(keywords),
      regenerateKeywords: false,
      // 公開回答圖片（維持原有介面）
      images: publicNewImages,
      existingImageIds: publicExistingImageIds,
      // 標籤回答卡片（含圖片資料）
      tagAnswerCards: tagCards.map(c => ({
        ...c,
        existingImageIds: (c.images || []).filter(img => img.isExisting).map(img => img.id),
        newImages: (c.images || []).filter(img => img.file && !img.isExisting),
      })),
      originalAnswerCards,
    };
  };

  // Handle save — 每張卡片的圖片資料打包進 dataToSave
  const handleSave = () => {
    if (validateForm() && !isLoading) {
      onSave(buildSaveData());
    }
  };

  // Helper function to extract URLs from text
  const extractUrls = (text) => {
    if (!text) return [];
    return text.match(urlRegex) || [];
  };

  const handleNewKeywordChange = (e) => setNewKeyword(e.target.value);

  const handleAddKeyword = () => {
    if (newKeyword.trim() !== '') {
      if (!keywords.includes(newKeyword.trim())) {
        setKeywords([...keywords, newKeyword.trim()]);
      }
      setNewKeyword('');
    }
  };

  const handleRemoveKeyword = (keywordToRemove) => {
    setKeywords(keywords.filter(keyword => keyword !== keywordToRemove));
  };

  const handleKeywordKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  // 每張卡片的圖片上傳處理
  const handleCardImageUpload = (cardIndex, event) => {
    const files = Array.from(event.target.files || []);
    setImageError('');

    const card = answerCards[cardIndex];
    const cardImages = card.images || [];
    const currentCount = cardImages.length;
    const newCount = files.length;

    if (currentCount >= 4) {
      setImageError(`此回答已達到 4 張圖片上限。`);
      resetFileInput(cardIndex);
      return;
    }

    if (currentCount + newCount > 4) {
      const allowed = 4 - currentCount;
      setImageError(`此回答目前已有 ${currentCount} 張，只能再上傳 ${allowed} 張。`);
      resetFileInput(cardIndex);
      return;
    }

    const validFiles = [];
    const errors = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (cardImages.length + validFiles.length >= 4) {
        errors.push(`已達到 4 張圖片上限，跳過剩餘 ${files.length - i} 個檔案`);
        break;
      }
      if (!file.type.startsWith('image/')) {
        errors.push(`${file.name} 不是圖片檔案`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        errors.push(`${file.name} 超過 10MB 限制`);
        continue;
      }
      validFiles.push(file);
    }

    if (errors.length > 0) setImageError(errors.join('；'));

    if (validFiles.length === 0) {
      resetFileInput(cardIndex);
      return;
    }

    validFiles.forEach((file, index) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAnswerCards(prev => {
          const updated = [...prev];
          const currentImages = updated[cardIndex].images || [];
          if (currentImages.length >= 4) return prev;
          updated[cardIndex] = {
            ...updated[cardIndex],
            images: [...currentImages, {
              id: Date.now() + Math.random() + index,
              url: reader.result,
              name: file.name,
              size: file.size,
              file: file,
              isExisting: false
            }]
          };
          return updated;
        });
      };
      reader.readAsDataURL(file);
    });

    resetFileInput(cardIndex);
  };

  const resetFileInput = (cardIndex) => {
    const key = `file-input-${cardIndex}`;
    if (fileInputRefs.current[key]) {
      fileInputRefs.current[key].value = '';
    }
  };

  // 移除卡片圖片
  const handleRemoveCardImage = (cardIndex, imageId) => {
    setAnswerCards(prev => {
      const updated = [...prev];
      updated[cardIndex] = {
        ...updated[cardIndex],
        images: (updated[cardIndex].images || []).filter(img => img.id !== imageId)
      };
      return updated;
    });
    setImageError('');
  };

  // 開啟圖片檢視器
  const handleImageClick = (cardIndex, imageIndex) => {
    const card = answerCards[cardIndex];
    const cardImages = (card?.images || []).filter(img => !img.loading);
    if (cardImages.length > 0) {
      setImageViewerImages(cardImages.map(img => ({
        ...img,
        fallbackUrl: img.isExisting ? `/api/images/proxy/${img.id}` : null
      })));
      setImageViewerIndex(imageIndex);
      setImageViewerOpen(true);
    }
  };

  // Extract URLs for display
  const questionUrls = extractUrls(question);
  const answerUrls = extractUrls(answer);

  const handleClose = () => {
    if (!isLoading) onClose();
  };

  // 渲染語音管理面板（Plan 068 Day 4）
  // 位於公開回答 TextField 與圖片上傳之間。狀態 chip 全部使用 NEUTRAL_CHIP_SX
  // 同一中性灰色，文字內容區分 ready / stale / pending / generating / failed / 未產生。
  const renderAudioPanel = () => {
    const status = audioInfo.status || 'missing';
    const label = AUDIO_STATUS_LABELS[status] || '未產生';
    const inFlight = status === 'pending' || status === 'generating';
    const hasAudio = !!audioInfo.filePath && (status === 'ready' || status === 'stale');
    // Cache-bust by the audio's audio_updated_at (encoded as a stable epoch
    // ms in the ?v= query). CloudFront's FaqAudioVersionedCache policy
    // includes ?v= in the cache key, so a new regen → new updatedAt → new
    // URL → cache miss → fresh fetch from S3. Repeat plays of the same
    // version hit the edge cache (efficient).
    const audioUrl = hasAudio && audioInfo.publicUrl
      ? `${audioInfo.publicUrl}?v=${audioInfo.updatedAt ? new Date(audioInfo.updatedAt).getTime() : audioInfo.duration || 0}`
      : null;
    // Pre-068 manual paths (e.g. "qa_audio/<name>") have a GSM on Asterisk
    // but no S3/CloudFront WAV mirror, so we can't preview-play them in the
    // browser. Surface this so 已就緒-without-player isn't misread as "audio
    // missing — must regenerate".
    const deployedNoPreview = hasAudio && !audioUrl;
    // Create mode (no faq.id yet): button label is 儲存並產生語音; it triggers
    // a save-with-_keepOpen so the dialog transitions to edit mode in-place.
    // Edit mode: button only regenerates audio (no embedding cost).
    const isCreate = !faq?.id;
    const editDirty = !isCreate && isFormDirty();
    const answerDirty = editDirty && isAnswerDirty();
    const buttonLabel = isCreate
      ? '儲存並產生語音'
      : (status === 'ready' || status === 'stale' ? '重新產生語音' : '產生語音');
    const buttonDisabled = audioBusy || inFlight || editDirty;

    return (
      <Box sx={{
        mt: 1.5, p: 1.5,
        bgcolor: '#fafafa',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: 1.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '13px', color: 'text.secondary', minWidth: 36 }}>
            語音
          </Typography>
          <Chip
            size="small"
            label={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                {inFlight && <CircularProgress size={10} thickness={6} sx={{ color: 'rgba(0,0,0,0.55)' }} />}
                <span>{label}</span>
              </Box>
            }
            sx={NEUTRAL_CHIP_SX}
            title={audioInfo.error || (audioInfo.duration ? `時長 ${audioInfo.duration} 秒` : undefined)}
          />
          {audioInfo.duration && (
            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
              {audioInfo.duration} 秒
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            disabled={buttonDisabled}
            onClick={handleRegenerateAudio}
            sx={{ fontSize: '12px', py: 0.25, px: 1, minHeight: 28 }}
          >
            {buttonLabel}
          </Button>
        </Box>
        {audioUrl && (
          <Box sx={{ mt: 1 }}>
            {/* key forces a fresh <audio> element after regen so the new file
                is fetched from CloudFront instead of the previously cached one.
                Default volume = 0.5 (middle) so playback isn't unexpectedly
                loud — set on the metadata-loaded event since `volume` cannot
                be set as a JSX attribute on <audio>. */}
            <audio
              key={audioUrl}
              src={audioUrl}
              controls
              preload="metadata"
              onLoadedMetadata={(e) => { e.target.volume = 0.5; }}
              style={{ width: '100%', height: 32 }}
            />
          </Box>
        )}
        {deployedNoPreview && (
          <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PhoneInTalkIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
              已部署於電話系統，無線上預覽
            </Typography>
          </Box>
        )}
        {audioError && (
          <FormHelperText error sx={{ mt: 0.5, fontSize: '12px' }}>
            {audioError}
          </FormHelperText>
        )}
        {status === 'failed' && audioInfo.error && (
          <FormHelperText error sx={{ mt: 0.5, fontSize: '12px' }} title={audioInfo.error}>
            上次產生失敗：{audioInfo.error.length > 60 ? `${audioInfo.error.slice(0, 60)}...` : audioInfo.error}
          </FormHelperText>
        )}
        {isCreate && (
          <FormHelperText sx={{ mt: 0.5, fontSize: '12px' }}>
            儲存後將自動產生語音（約需 15 秒）
          </FormHelperText>
        )}
        {editDirty && (
          <FormHelperText sx={{ mt: 0.5, fontSize: '12px', color: 'text.secondary' }}>
            {answerDirty
              ? '儲存後會自動更新語音（約需 15 秒）'
              : '有未儲存的修改，請先按「儲存」再產生語音'}
          </FormHelperText>
        )}
      </Box>
    );
  };

  // 渲染每張卡片內的圖片上傳區域
  const renderCardImages = (card, cardIndex) => {
    const cardImages = card.images || [];
    const fileInputKey = `file-input-${cardIndex}`;

    return (
      <Box sx={{ mt: 1.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {/* 圖片縮圖 */}
          {cardImages.map((image, imgIndex) => (
            <Paper
              key={image.id}
              elevation={1}
              sx={{
                width: 100, height: 100,
                position: 'relative', overflow: 'hidden',
                borderRadius: 1,
                border: image.isExisting ? '1.5px solid #1976d2' : '1.5px solid #e0e0e0',
                cursor: image.loading ? 'default' : 'pointer',
                transition: 'transform 0.15s, box-shadow 0.15s',
                '&:hover': {
                  transform: image.loading ? 'none' : 'scale(1.08)',
                  boxShadow: image.loading ? 1 : 4,
                }
              }}
              onClick={() => !image.loading && handleImageClick(cardIndex, imgIndex)}
            >
              {image.loading ? (
                <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <img
                  src={image.url}
                  alt={image.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    if (!image.fallbackAttempted && image.isExisting) {
                      e.target.src = `/api/images/proxy/${image.id}`;
                      setAnswerCards(prev => {
                        const updated = [...prev];
                        updated[cardIndex] = {
                          ...updated[cardIndex],
                          images: updated[cardIndex].images.map(img =>
                            img.id === image.id ? { ...img, fallbackAttempted: true } : img
                          )
                        };
                        return updated;
                      });
                    }
                  }}
                />
              )}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveCardImage(cardIndex, image.id);
                }}
                disabled={isLoading || image.loading}
                sx={{
                  position: 'absolute', top: 0, right: 0,
                  bgcolor: 'rgba(255,255,255,0.85)', p: '2px',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' }
                }}
              >
                <DeleteIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Paper>
          ))}

          {/* 上傳按鈕 */}
          {cardImages.length < 4 && (
            <Paper
              component="label"
              elevation={0}
              sx={{
                width: 100, height: 100,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: isLoading ? 'default' : 'pointer',
                borderRadius: 1,
                border: '1.5px dashed #bdbdbd',
                bgcolor: '#fafafa',
                transition: 'all 0.15s',
                '&:hover': {
                  bgcolor: isLoading ? '#fafafa' : '#f0f0f0',
                  borderColor: isLoading ? '#bdbdbd' : '#999'
                }
              }}
            >
              <input
                ref={el => fileInputRefs.current[fileInputKey] = el}
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => handleCardImageUpload(cardIndex, e)}
                style={{ display: 'none' }}
                disabled={isLoading}
              />
              <AddPhotoAlternateIcon sx={{ fontSize: 32, color: isLoading ? '#ccc' : '#888' }} />
            </Paper>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          已上傳 {cardImages.length}/4 (每張最大 10MB)
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        disableEscapeKeyDown={isLoading}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 'bold' }}>
              {faq?.id ? '編輯常見問題' : '新增常見問題'}
            </Typography>
            <IconButton onClick={handleClose} size="small" disabled={isLoading}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* 編號 */}
          {faq?.id && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: '20px', fontWeight: 'bold', mb: 1 }}>
                編號：{faq.displayNumber}
              </Typography>
            </Box>
          )}

          {/* 分類 */}
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '20px', fontWeight: 'bold', mb: 1 }}>分類</Typography>
            <TextField
              fullWidth
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              variant="outlined"
              placeholder="請輸入分類"
              disabled={isLoading}
              InputProps={{ style: { fontSize: '18px' } }}
            />
            <FormHelperText>此欄位為選填，可留空</FormHelperText>
          </Box>

          {/* 問題 */}
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '20px', fontWeight: 'bold', mb: 1 }}>問題</Typography>
            <TextField
              fullWidth
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              variant="outlined"
              placeholder="請輸入問題"
              error={!!questionError}
              helperText={questionError}
              multiline
              rows={2}
              disabled={isLoading}
              InputProps={{ style: { fontSize: '18px' } }}
              inputRef={questionInputRef}
            />
            {questionUrls.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">網址預覽:</Typography>
                <Box sx={{ mt: 0.5 }}>
                  {questionUrls.map((url, index) => (
                    <Link key={`q-url-${index}`} href={url} target="_blank" rel="noopener noreferrer"
                      sx={{ display: 'flex', alignItems: 'center', fontSize: '0.85rem', mb: 0.5, color: 'primary.main', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <OpenInNewIcon fontSize="small" sx={{ mr: 0.5 }} />{url}
                    </Link>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* 回答 */}
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '20px', fontWeight: 'bold', mb: 1 }}>回答</Typography>

            {/* 回答卡片列表 */}
            {answerCards.map((card, cardIndex) => (
              <Paper
                key={card.type === 'public' ? 'public' : `tag-${card.tagId}`}
                variant="outlined"
                sx={{
                  p: 2, mb: 1.5,
                  borderColor: card.type === 'public' ? '#4caf50' : (card.tagColor || '#607D8B'),
                  borderWidth: 1.5,
                  borderRadius: 2,
                }}
              >
                {/* 卡片標頭 */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {card.type === 'public' ? (
                      <Chip
                        icon={<PublicIcon sx={{ fontSize: 16 }} />}
                        label="公開 (預設)"
                        size="small"
                        sx={{
                          bgcolor: '#e8f5e9', color: '#2e7d32',
                          fontWeight: 600, fontSize: '13px',
                          '& .MuiChip-icon': { color: '#2e7d32' }
                        }}
                      />
                    ) : (
                      <Chip
                        icon={<LabelIcon sx={{ fontSize: 14 }} />}
                        label={card.tagName}
                        size="small"
                        sx={{
                          bgcolor: `${card.tagColor}20`,
                          color: card.tagColor,
                          border: `1px solid ${card.tagColor}60`,
                          fontWeight: 600, fontSize: '13px',
                          '& .MuiChip-icon': { color: card.tagColor }
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {card.type === 'tag' && answerCards.some(c => c.type === 'public' && c.answer?.trim()) && (
                      <Tooltip title="複製公開回答">
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => {
                            const pub = answerCards.find(c => c.type === 'public');
                            if (pub) {
                              const updated = [...answerCards];
                              updated[cardIndex] = { ...updated[cardIndex], answer: pub.answer };
                              setAnswerCards(updated);
                            }
                          }}
                          disabled={isLoading}
                          sx={{ minWidth: 'auto', fontSize: '12px', px: 1, color: 'text.secondary' }}
                        >
                          複製公開回答
                        </Button>
                      </Tooltip>
                    )}
                    {answerCards.length > 1 && (
                      <Tooltip title="刪除此回答">
                        <IconButton
                          size="small"
                          onClick={() => setAnswerCards(prev => prev.filter((_, i) => i !== cardIndex))}
                          disabled={isLoading}
                          sx={{ color: 'text.secondary' }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                {/* 回答文字 */}
                <TextField
                  fullWidth
                  value={card.answer}
                  onChange={(e) => {
                    const updated = [...answerCards];
                    updated[cardIndex] = { ...updated[cardIndex], answer: e.target.value };
                    setAnswerCards(updated);
                    if (card.type === 'public') setAnswer(e.target.value);
                  }}
                  variant="outlined"
                  placeholder={card.type === 'public' ? '請輸入公開回答' : `請輸入 ${card.tagName} 的專屬回答`}
                  multiline
                  rows={4}
                  disabled={isLoading}
                  InputProps={{ style: { fontSize: '16px' } }}
                />

                {/* Plan 068 Day 4 — 公開回答的語音管理面板，於開通語音功能
                    時顯示在 TextField 與圖片上傳之間。在新增模式下也顯示，
                    button 變為「儲存並產生語音」，後端 createFaq 自動 enqueue
                    （見 add-faq-audio-on-create change）。 */}
                {card.type === 'public' && hasFeature('voiceEnabled') &&
                  renderAudioPanel()}

                {/* 每張卡片的圖片上傳區域 */}
                {renderCardImages(card, cardIndex)}
              </Paper>
            ))}

            {/* 圖片錯誤提示 */}
            {imageError && (
              <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setImageError('')}>
                {imageError}
              </Alert>
            )}

            {/* 警告：沒有公開回答 */}
            {!answerCards.some(c => c.type === 'public') && answerCards.length > 0 && (
              <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 1.5 }}>
                沒有公開回答。未標籤的用戶將不會收到此回答。
              </Alert>
            )}

            {answerError && (
              <FormHelperText error sx={{ mb: 1 }}>{answerError}</FormHelperText>
            )}

            {/* 新增回答按鈕 */}
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => { setShowAnswerPicker(!showAnswerPicker); setShowNewTagForm(false); }}
              disabled={isLoading}
              sx={{
                borderStyle: 'dashed', borderColor: '#bdbdbd', color: 'text.secondary',
                py: 1.2, mb: 1, '&:hover': { borderColor: '#757575', bgcolor: '#fafafa' }
              }}
            >
              新增回答
            </Button>

            {/* 回答選擇器 */}
            <Collapse in={showAnswerPicker}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>選擇回答類型</Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                  {!answerCards.some(c => c.type === 'public') && (
                    <Chip
                      icon={<PublicIcon sx={{ fontSize: 16 }} />}
                      label="公開 (預設)"
                      clickable
                      onClick={() => {
                        setAnswerCards(prev => [{ type: 'public', answer: '', images: [], existingImageIds: [] }, ...prev]);
                        setShowAnswerPicker(false);
                      }}
                      sx={{
                        bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 500,
                        '&:hover': { bgcolor: '#c8e6c9' },
                        '& .MuiChip-icon': { color: '#2e7d32' }
                      }}
                    />
                  )}

                  {availableTags
                    .filter(tag => tag.isActive !== false)
                    .filter(tag => !answerCards.some(c => c.type === 'tag' && c.tagId === tag.id))
                    .map(tag => (
                      <Chip
                        key={tag.id}
                        icon={<LabelIcon sx={{ fontSize: 14 }} />}
                        label={tag.name}
                        clickable
                        onClick={() => {
                          setAnswerCards(prev => [...prev, {
                            type: 'tag', tagId: tag.id,
                            tagName: tag.name, tagColor: tag.color || '#607D8B',
                            answer: '', images: [], existingImageIds: []
                          }]);
                          setShowAnswerPicker(false);
                        }}
                        sx={{
                          bgcolor: `${tag.color || '#607D8B'}20`,
                          color: tag.color || '#607D8B',
                          border: `1px solid ${tag.color || '#607D8B'}60`,
                          fontWeight: 500,
                          '&:hover': { bgcolor: `${tag.color || '#607D8B'}35` },
                          '& .MuiChip-icon': { color: tag.color || '#607D8B' }
                        }}
                      />
                    ))
                  }
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {!showNewTagForm ? (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setShowNewTagForm(true)}
                    sx={{ color: 'text.secondary' }}
                  >
                    建立新標籤
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField
                      size="small"
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      placeholder="標籤名稱"
                      fullWidth
                      disabled={tagLoading}
                    />
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {['#F44336', '#E91E63', '#9C27B0', '#3F51B5', '#2196F3', '#009688', '#FF9800', '#607D8B'].map(color => (
                        <Box
                          key={color}
                          onClick={() => setNewTagColor(color)}
                          sx={{
                            width: 28, height: 28, borderRadius: '50%',
                            bgcolor: color, cursor: 'pointer',
                            border: newTagColor === color ? '3px solid #333' : '2px solid transparent',
                            transition: 'border 0.15s',
                            '&:hover': { transform: 'scale(1.1)' }
                          }}
                        />
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={!newTagName.trim() || tagLoading}
                        onClick={async () => {
                          try {
                            setTagLoading(true);
                            const res = await tagService.createTag({ name: newTagName.trim(), color: newTagColor });
                            const newTag = res.data || res;
                            setAvailableTags(prev => [...prev, newTag]);
                            setAnswerCards(prev => [...prev, {
                              type: 'tag', tagId: newTag.id,
                              tagName: newTag.name, tagColor: newTag.color || newTagColor,
                              answer: '', images: [], existingImageIds: []
                            }]);
                            setNewTagName('');
                            setNewTagColor('#607D8B');
                            setShowNewTagForm(false);
                            setShowAnswerPicker(false);
                          } catch (e) {
                            // 標籤建立失敗
                          } finally {
                            setTagLoading(false);
                          }
                        }}
                      >
                        {tagLoading ? <CircularProgress size={18} /> : '建立並使用'}
                      </Button>
                      <Button
                        size="small"
                        onClick={() => { setShowNewTagForm(false); setNewTagName(''); }}
                        disabled={tagLoading}
                      >
                        取消
                      </Button>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Collapse>

            <FormHelperText sx={{ mt: 1 }}>
              系統會依照用戶標籤回覆對應的回答。沒有符合標籤的用戶收到公開回答。
            </FormHelperText>

            {answerUrls.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">網址預覽:</Typography>
                <Box sx={{ mt: 0.5 }}>
                  {answerUrls.map((url, index) => (
                    <Link key={`a-url-${index}`} href={url} target="_blank" rel="noopener noreferrer"
                      sx={{ display: 'flex', alignItems: 'center', fontSize: '0.85rem', mb: 0.5, color: 'primary.main', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <OpenInNewIcon fontSize="small" sx={{ mr: 0.5 }} />{url}
                    </Link>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* 關鍵詞 */}
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '20px', fontWeight: 'bold', mb: 1 }}>關鍵詞</Typography>

            <Box sx={{ display: 'flex', mb: 2 }}>
              <TextField
                fullWidth
                value={newKeyword}
                onChange={handleNewKeywordChange}
                onKeyPress={handleKeywordKeyPress}
                variant="outlined"
                placeholder="輸入關鍵詞後按 Enter 或新增按鈕"
                disabled={isLoading}
                InputProps={{ style: { fontSize: '16px' } }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddKeyword}
                disabled={!newKeyword.trim() || isLoading}
                sx={{ ml: 1, minWidth: 90 }}
              >
                <AddIcon fontSize="small" sx={{ mr: 0.5 }} />
                新增
              </Button>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: 40 }}>
              {keywords.length > 0 ? (
                keywords.map((keyword, index) => (
                  <Chip
                    key={index}
                    label={keyword}
                    onDelete={isLoading ? undefined : () => handleRemoveKeyword(keyword)}
                    color="primary"
                    variant="outlined"
                    size="medium"
                    disabled={isLoading}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">尚未新增關鍵詞</Typography>
              )}
            </Box>

            <FormHelperText>
              關鍵詞可幫助搜尋和分類問題，此關鍵詞來源為您上傳的 Excel 檔案，或由 AI 自動生成。
            </FormHelperText>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={handleClose} disabled={isLoading}>
            取消
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isLoading}
            sx={{ position: 'relative', minWidth: '64px' }}
          >
            {isLoading && (
              <CircularProgress
                size={24}
                sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-12px', marginLeft: '-12px' }}
              />
            )}
            <span style={{ visibility: isLoading ? 'hidden' : 'visible' }}>儲存</span>
          </Button>
        </DialogActions>
      </Dialog>

      {/* ImageViewer */}
      <ImageViewer
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        images={imageViewerImages}
        initialIndex={imageViewerIndex}
      />
    </>
  );
};

export default FaqEditDialog;
